import { LineNumbers, Lottery, LotteryRanges } from '../Types/Types';

import { randomNumber } from 'utils/helpers';

export const PRIMARY_NUMBERS = 0;
export const BONUS_NUMBERS = 1;
export const RANGE_FROM = 0;
export const RANGE_TO = 1;
export const MIN_NUMBERS_IN_POOL = 0;
export const MAX_NUMBERS_IN_POOL = 1;

export const compareNumbers = (a: number, b: number): number => a - b;

export function isNonEmptyLine(line: LineNumbers): boolean {
    return line.some((numbers) => numbers.length > 0);
}

export function getNonEmptyLines(lines: LineNumbers[]): LineNumbers[] {
    return lines.filter((line) => isNonEmptyLine(line));
}

export function countToLastNonEmptyLine(lines: LineNumbers[]): number {
    const nonEmpty = [...lines].reverse().findIndex((line) => isNonEmptyLine(line));

    return nonEmpty > -1 ? lines.length - nonEmpty : 0;
}

export function isBonusNumberPickable(lottery: Lottery): boolean {
    return (
        !lottery.isGeneratingRandomBonusNumbers ||
        (lottery.isGeneratingRandomBonusNumbers &&
            lottery.randomBonusNumbersGeneratingMethods?.[0] === 'user_manual_pick')
    );
}

export function isValidLine(lottery: Lottery, line: LineNumbers, numbersPerLine: number[]): boolean {
    const numbersPerLineToCheck = isBonusNumberPickable(lottery) ? numbersPerLine : [numbersPerLine[0]];

    const lineToCheck = isBonusNumberPickable(lottery) ? line : [line[0]];

    return (
        lineToCheck.length > 0 && lineToCheck.every((numbers, index) => numbers.length === numbersPerLineToCheck[index])
    );
}

export function isPickValidLine(line: LineNumbers, numbersPerLine: number) {
    return line.length === numbersPerLine && line.every((subArray) => Array.isArray(subArray) && subArray.length === 1);
}

export function isValidStake(stake: number, stakeMin: number, stakeMax: number): boolean {
    if (stakeMin <= 0) {
        console.error('Min stake should be greater than 0.');
        return false;
    }

    if (stakeMax < stakeMin) {
        console.error('Max stake cannot be lower than min stake.');
        return false;
    }

    if (stake >= stakeMin && stake <= stakeMax) {
        return true;
    }
    console.error(`Stake ${stake} has to be between ${stakeMin} and ${stakeMax}.`);
    return false;
}

export function getValidLines(lottery: Lottery, lines: LineNumbers[], numbersPerLine: number[]): LineNumbers[] {
    return lines.filter((line) => isValidLine(lottery, line, numbersPerLine));
}

export function getValidLinesAndStakes(
    lottery: Lottery,
    lines: LineNumbers[],
    stakes: number[],
    numbersPerLine: number[],
    stakeMin: number,
    stakeMax: number
): { validLines: LineNumbers[]; validStakes: number[] } {
    const validLines: LineNumbers[] = [];
    const validStakes: number[] = [];
    const defaultStake = Math.max(1.0, stakeMin);

    lines.forEach((line, index) => {
        if (isValidLine(lottery, line, numbersPerLine)) {
            // Line is valid
            const stake = stakes[index];

            if (isValidStake(stake, stakeMin, stakeMax)) {
                // Stake is valid
                validStakes.push(stake);
            } else {
                // Stake is invalid, use default stake
                console.error(`Invalid stake ${stake} at index ${index}, using default ${defaultStake}.`);
                validStakes.push(defaultStake);
            }

            validLines.push(line);
        }
        // If the line is invalid, we skip it entirely
    });

    return { validLines, validStakes };
}

export function generateRandomLine(ranges: LotteryRanges, numbersPerLine: number[]): LineNumbers {
    return ranges.map((range, index) => {
        const [from, to] = range;
        const numbers = new Set<number>();

        if (numbersPerLine[index] > to - from + 1) {
            throw new Error(`Range (${from}, ${to}) is too small to generate ${numbersPerLine[index]} unique numbers.`);
        }

        while (numbers.size < numbersPerLine[index]) {
            numbers.add(randomNumber(from, to));
        }

        return [...numbers].sort(compareNumbers);
    });
}

export function toggleNumber(numbers: number[], targetNumber: number, limit: number): number[] {
    if (numbers.includes(targetNumber)) {
        return numbers.filter((value) => value !== targetNumber);
    }

    return numbers.length < limit ? [...numbers, targetNumber].sort(compareNumbers) : numbers;
}

export function shouldUseLineBuyPrice(lotteryCurrencyCode: string): boolean {
    const actualUsedCurrencyCodeByUser = document.getElementsByTagName('html')[0].getAttribute('data-currency');
    return lotteryCurrencyCode !== actualUsedCurrencyCodeByUser;
}

export function calculateMaxCombinations(
    ticketPrize: number,
    limit: number,
    fixedNumber: boolean = false,
    numbersPerLine: number,
    maxNumbersToPick: number
): number {
    for (let currentN = numbersPerLine; currentN <= maxNumbersToPick; currentN++) {
        const numberOfCombinations = fixedNumber ? currentN - 1 : (currentN * (currentN - 1)) / 2;
        const cost = numberOfCombinations * ticketPrize;

        if (cost > limit) {
            return fixedNumber ? currentN - 2 : ((currentN - 1) * (currentN - 2)) / 2;
        }
    }
    return fixedNumber ? maxNumbersToPick - 1 : (maxNumbersToPick * (maxNumbersToPick - 1)) / 2;
}

export function generatePermutations(
    numbers: number[],
    length: number,
    requiredNumber: number | null = null
): LineNumbers {
    if (numbers.length === 0 || length === 0 || length > numbers.length) {
        return [];
    }

    if (requiredNumber !== null) {
        const remainingNumbers = numbers.filter((n) => n !== requiredNumber);

        if (remainingNumbers.length === numbers.length) {
            return [];
        }

        const baseCombinations = generateCombinations(remainingNumbers, length - 1);
        return baseCombinations.map((combo) => {
            const position = Math.floor(Math.random() * length);
            return [...combo.slice(0, position), requiredNumber, ...combo.slice(position)];
        });
    }

    return generateCombinations(numbers, length);
}

function generateCombinations(numbers: number[], length: number): LineNumbers {
    if (length === 0) {
        return [[]];
    }

    if (length === 1) {
        return numbers.map((n) => [n]);
    }

    return numbers.reduce((acc: LineNumbers, current: number, index: number) => {
        const subCombinations = generateCombinations(numbers.slice(index + 1), length - 1);

        return [...acc, ...subCombinations.map((subComb) => [current, ...subComb])];
    }, []);
}
